import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Divider } from '@material-ui/core';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { FaRegNewspaper } from 'react-icons/fa';
import { FiPhone } from 'react-icons/fi';
import { HiMailOpen } from 'react-icons/hi';
import { IoMdCreate } from 'react-icons/io';
import { MdAttachMoney } from 'react-icons/md';
import { BiImage } from 'react-icons/bi';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import api from '../../services/api';

import * as S from './styles';

import Input from '../../newComponents/UnformInput';
import Select from '../../newComponents/UnformSelect';
import Button from '../../newComponents/Button';
import FileInput from '../../newComponents/FileInput';

import getValidationErrors from '../../utils/getValidationErrors';

interface IOption {
  id: string;
  value: number;
  label: string;
}

interface ILojaData {
  nomeFantasia: string;
  nomeFantasiaGrupo: string;
  telefone: string;
  email: string;
  valorMinimoPedido: string | null;
  inscricaoMunicipal: string;
  inscricaoEstadual: string;
  logomarca: string;
}

const limitacaoOptions: IOption[] = [
  { id: '0', value: 0, label: 'Regional' },
  { id: '1', value: 1, label: 'Nacional' },
];

const Loja: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [slug, setSlug] = useState('');
  const [selectedOption, setSelectedOption] = useState<IOption>(
    limitacaoOptions[0],
  );
  const [lojaData, setLojaData] = useState<ILojaData>({
    nomeFantasia: '',
    nomeFantasiaGrupo: '',
    telefone: '',
    email: '',
    valorMinimoPedido: null,
    inscricaoMunicipal: '',
    inscricaoEstadual: '',
    logomarca: '',
  });

  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);

  const handleSubmit = useCallback(
    async (data: ILojaData): Promise<void> => {
      setIsLoading(true);

      try {
        formRef.current?.setErrors({});

        await api.put(`/empresa/${user.empresa_id}`, {
          nome_fantasia: data.nomeFantasia,
          nome_fantasia_grupo: data.nomeFantasiaGrupo || data.nomeFantasia,
          telefone: data.telefone,
          email: data.email,
          valor_minimo_pedido: data.valorMinimoPedido || 0,
          inscricao_municipal: data.inscricaoMunicipal,
          inscricao_estadual: data.inscricaoEstadual,
          limitacao: selectedOption.value,
          slug,
          keepTimezone: true,
        });

        if (!data.nomeFantasiaGrupo || data.nomeFantasiaGrupo.length <= 0) {
          formRef.current?.setFieldValue(
            'nomeFantasiaGrupo',
            `${data.nomeFantasia}`,
          );
        }

        addToast({
          type: 'success',
          title: 'Dados atualizados!',
          description: 'Os dados de sua loja foram atualizados com sucesso.',
        });
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
        } else if ((error as any).response?.status === 405) {
          addToast({
            type: 'error',
            title: 'Não Autorizado',
          });
        } else {
          addToast({
            type: 'error',
            title: 'Erro ao editar dados',
            description:
              'Ocorreu um erro ao editar os dados. Verifique os dados no formulário.',
          });
        }
        setIsLoading(false);
      }
    },
    [user.empresa_id, selectedOption.value, slug, addToast],
  );

  const handleSlugChange = useCallback((value: string) => {
    let parsedValue = value;

    if (parsedValue.length === 1) {
      parsedValue = parsedValue.replace(/^\s+|\s+$/g, '');
    }

    parsedValue = parsedValue.toLowerCase();

    const from = 'àáãäâèéëêìíïîòóöôùúüûñç·/_,:;';
    const to = 'aaaaaeeeeiiiioooouuuunc------';

    from.split('').map((_, index) => {
      parsedValue = parsedValue.replace(
        new RegExp(from.charAt(index), 'g'),
        to.charAt(index),
      );
    });

    parsedValue = parsedValue
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

    setSlug(parsedValue);
  }, []);

  const handleLogotipoChange = async (file: File): Promise<void> => {
    if (file) {
      const data = new FormData();
      data.append('logotipo_img', file);

      try {
        const response = await api.patch(`/empresa/${user.empresa_id}`, data);

        setLojaData((oldState) => ({
          ...oldState,
          logomarca: response.data.logomarca,
        }));

        addToast({
          type: 'success',
          title: 'Logotipo Atualizado',
        });
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Logotipo não atualizado.',
        });
      }
    }
  };

  useEffect(() => {
    async function loadData(): Promise<void> {
      const response = await api.get(`/empresa/${user.empresa_id}`);

      setLojaData({
        nomeFantasia: response.data.nome_fantasia,
        nomeFantasiaGrupo: response.data.nome_fantasia_grupo,
        telefone: response.data.telefone,
        email: response.data.email,
        valorMinimoPedido: response.data.valor_minimo_pedido,
        inscricaoMunicipal: response.data.inscricao_municipal,
        inscricaoEstadual: response.data.inscricao_estadual,
        logomarca: response.data.logomarca,
      });

      setSelectedOption(
        limitacaoOptions.find(
          (option) => option.value === response.data.limitacao,
        )!,
      );

      setSlug(response.data.slug || '');
    }

    loadData();
  }, [user.empresa_id]);

  return (
    <S.Container>
      <div className="header">
        <h2>Edite os dados da sua loja</h2>
        <p>
          Número da Loja: <span>{user.empresa_id}</span>
        </p>
      </div>

      <Divider />

      <Form ref={formRef} onSubmit={handleSubmit} initialData={lojaData}>
        <div className="image">
          <FileInput
            id="updateImageFileInput"
            icon={BiImage}
            title="Escolha uma imagem"
            selectedImage={lojaData.logomarca}
            acceptableTypes=".jpeg,.jpg,.png"
            subtitle="Formatos: .jpeg, .jpg, .png"
            containerStyle={{
              width: 'auto',
              maxWidth: 300,
              margin: '0 auto',
            }}
            onRemove={() => {
              //
            }}
            onSelectFile={(file) => handleLogotipoChange(file)}
          />
        </div>

        <div className="information">
          <div>
            <Input
              name="nomeFantasia"
              icon={FaRegNewspaper}
              label="Nome fantasia:"
              placeholder="Ex.: Digite o nome fantasia da sua loja"
              disabled={isLoading}
            />

            <Input
              name="nomeFantasiaGrupo"
              icon={FaRegNewspaper}
              label="Nome fantasia do grupo:"
              placeholder="Ex.: Digite o nome fantasia do grupo da sua empresa"
              disabled={isLoading}
            />

            <Input
              name="telefone"
              icon={FiPhone}
              label="Telefone:"
              placeholder="Ex.: Digite o telefone da sua loja"
              disabled={isLoading}
            />
          </div>
          <div>
            <Input
              name="email"
              icon={HiMailOpen}
              label="E-mail:"
              placeholder="Ex.: E-mail da sua loja"
              disabled={isLoading}
            />

            <Input
              name="inscricaoEstadual"
              icon={IoMdCreate}
              label="Inscrição estadual:"
              disabled={isLoading}
            />

            <Input
              name="inscricaoMunicipal"
              icon={IoMdCreate}
              label="Inscrição municipal:"
              disabled={isLoading}
            />
          </div>
          <div>
            <div className="limitacao">
              <label htmlFor="limitacao">Limitação:</label>
              <Select
                name="Limitacao"
                value={selectedOption}
                disabled={isLoading}
                options={limitacaoOptions}
                onChange={(value: any) => setSelectedOption(value)}
              />
            </div>

            <Input
              name="valorMinimoPedido"
              icon={MdAttachMoney}
              label="Pedido mínimo:"
              disabled={isLoading}
              type="number"
            />
          </div>

          <div>
            <Input
              name="slug"
              label="URL da loja:"
              placeholder="Ex.: lojademo"
              disabled={isLoading}
              preffix="https://atacdelivery.com/"
              value={slug}
              onChange={(event) => handleSlugChange(event.target.value)}
            />
          </div>
        </div>
        <Button full type="submit" disabled={isLoading}>
          Salvar
        </Button>
      </Form>
    </S.Container>
  );
};

export default Loja;
